import { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { useFormIntegration, type IFormIntegrationConfig } from '~/hooks/useFormIntegration';
import { useHubSpotProperties, type IHubSpotProperty } from '~/hooks/useHubSpotProperties';
import type { IFormSchema } from '~/types';

interface IFieldMapping {
  formField: string;
  hubspotProperty: string;
}

interface IHubSpotFormConfigProps {
  formId: string;
  integrationId: string;
  formSchema: IFormSchema;
}

function FieldMappingRow({
  mapping,
  formFields,
  hubspotProperties,
  onChange,
  onRemove,
}: {
  mapping: IFieldMapping;
  formFields: string[];
  hubspotProperties: IHubSpotProperty[];
  onChange: (updated: IFieldMapping) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <select
        className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
        value={mapping.formField}
        onChange={(e) => onChange({ ...mapping, formField: e.target.value })}
      >
        <option value="">Select form field...</option>
        {formFields.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground text-sm shrink-0">maps to</span>
      <select
        className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
        value={mapping.hubspotProperty}
        onChange={(e) => onChange({ ...mapping, hubspotProperty: e.target.value })}
      >
        <option value="">Select HubSpot property...</option>
        {hubspotProperties.map((p) => (
          <option key={p.name} value={p.name}>
            {p.label} ({p.name})
          </option>
        ))}
      </select>
      <Button variant="ghost" size="sm" onClick={onRemove}>
        <Trash2 className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  );
}

export const HubSpotFormConfig = ({ formId, integrationId, formSchema }: IHubSpotFormConfigProps) => {
  const { formIntegration, loading, error, save } = useFormIntegration(formId, integrationId);
  const { contactProperties, companyProperties, loading: propsLoading } = useHubSpotProperties(true);

  const [enabled, setEnabled] = useState(false);
  const [contactMappings, setContactMappings] = useState<IFieldMapping[]>([]);
  const [companyMappings, setCompanyMappings] = useState<IFieldMapping[]>([]);
  const [syncPartialFills, setSyncPartialFills] = useState(true);
  const [partialFillDelay, setPartialFillDelay] = useState(10);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Initialize state from existing config
  useEffect(() => {
    if (formIntegration) {
      setEnabled(formIntegration.enabled);
      const config = formIntegration.config;
      setContactMappings(config.contactFieldMappings ?? []);
      setCompanyMappings(config.companyFieldMappings ?? []);
      setSyncPartialFills(config.syncPartialFills ?? true);
      setPartialFillDelay(config.partialFillDelayMinutes ?? 10);
    }
  }, [formIntegration]);

  const formFields = Object.keys(formSchema.properties ?? {});

  const handleSave = async () => {
    setSaving(true);
    try {
      const config: IFormIntegrationConfig = {
        contactFieldMappings: contactMappings.filter((m) => m.formField && m.hubspotProperty),
        companyFieldMappings: companyMappings.filter((m) => m.formField && m.hubspotProperty),
        syncPartialFills,
        partialFillDelayMinutes: partialFillDelay,
      };
      await save(enabled, config);
      setDirty(false);
    } catch {
      // error is set in the hook
    } finally {
      setSaving(false);
    }
  };

  const markDirty = () => setDirty(true);

  const updateContactMapping = (index: number, updated: IFieldMapping) => {
    const next = [...contactMappings];
    next[index] = updated;
    setContactMappings(next);
    markDirty();
  };

  const updateCompanyMapping = (index: number, updated: IFieldMapping) => {
    const next = [...companyMappings];
    next[index] = updated;
    setCompanyMappings(next);
    markDirty();
  };

  if (loading || propsLoading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const mappingCount =
    contactMappings.filter((m) => m.formField && m.hubspotProperty).length +
    companyMappings.filter((m) => m.formField && m.hubspotProperty).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <CardTitle className="text-base">HubSpot Sync</CardTitle>
              {!enabled && <CardDescription>Configure how this form syncs to HubSpot CRM</CardDescription>}
            </div>
            {enabled && !expanded && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-green-700 font-medium">Active</span>
                {mappingCount > 0 && (
                  <span>
                    &middot; {mappingCount} field {mappingCount === 1 ? 'mapping' : 'mappings'}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {enabled && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {expanded ? 'Collapse' : 'Edit'}
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={async (e) => {
                  const newEnabled = e.target.checked;
                  setEnabled(newEnabled);
                  if (newEnabled) {
                    setExpanded(true);
                    markDirty();
                  } else {
                    setExpanded(false);
                    setSaving(true);
                    try {
                      const config: IFormIntegrationConfig = {
                        contactFieldMappings: contactMappings.filter((m) => m.formField && m.hubspotProperty),
                        companyFieldMappings: companyMappings.filter((m) => m.formField && m.hubspotProperty),
                        syncPartialFills,
                        partialFillDelayMinutes: partialFillDelay,
                      };
                      await save(false, config);
                      setDirty(false);
                    } catch {
                      /* error is set in the hook */
                    } finally {
                      setSaving(false);
                    }
                  }
                }}
                className="rounded"
              />
              <span className="text-sm font-medium">Enabled</span>
            </label>
          </div>
        </div>
      </CardHeader>

      {enabled && expanded && (
        <CardContent className="space-y-6">
          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Contact Field Mappings */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Contact Field Mappings</h4>
            {contactMappings.map((mapping, i) => (
              <FieldMappingRow
                key={i}
                mapping={mapping}
                formFields={formFields}
                hubspotProperties={contactProperties}
                onChange={(u) => updateContactMapping(i, u)}
                onRemove={() => {
                  setContactMappings(contactMappings.filter((_, j) => j !== i));
                  markDirty();
                }}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setContactMappings([...contactMappings, { formField: '', hubspotProperty: '' }]);
                markDirty();
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> Add Contact Mapping
            </Button>
          </div>

          {/* Company Field Mappings */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Company Field Mappings</h4>
            {companyMappings.map((mapping, i) => (
              <FieldMappingRow
                key={i}
                mapping={mapping}
                formFields={formFields}
                hubspotProperties={companyProperties}
                onChange={(u) => updateCompanyMapping(i, u)}
                onRemove={() => {
                  setCompanyMappings(companyMappings.filter((_, j) => j !== i));
                  markDirty();
                }}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCompanyMappings([...companyMappings, { formField: '', hubspotProperty: '' }]);
                markDirty();
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> Add Company Mapping
            </Button>
          </div>

          {/* Partial Fill Settings */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Partial Fill Sync</h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={syncPartialFills}
                onChange={(e) => {
                  setSyncPartialFills(e.target.checked);
                  markDirty();
                }}
                className="rounded"
              />
              <span className="text-sm">Sync incomplete submissions after inactivity</span>
            </label>
            {syncPartialFills && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Delay:</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={partialFillDelay}
                  onChange={(e) => {
                    setPartialFillDelay(Number(e.target.value));
                    markDirty();
                  }}
                  className="w-20 rounded-md border bg-background px-3 py-1 text-sm"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={async () => {
                await handleSave();
                setExpanded(false);
              }}
              disabled={saving || !dirty}
              size="sm"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
